import apiClient from '@/lib/api-client';
import type {
  Subscription,
  CreateSubscriptionRequest,
  ChangeSubscriptionRequest,
  SubscriptionResponse,
  SubscriptionLimits,
  ApiPlan,
  ApiPlansResponse,
  Currency,
  PromoCodeValidationResponse,
  SubscriptionPlan,
  BillingInterval,
  BillingStatus,
} from '@/types/subscription';

class SubscriptionService {
  /**
   * Create a new subscription for the organization
   */
  async createSubscription(data: CreateSubscriptionRequest): Promise<SubscriptionResponse> {
    const { data: response } = await apiClient.post<{ data: any }>(
      '/v1/subscriptions',
      {
        subscription: {
          plan_id: data.planId,
          billing_cycle: data.billingCycle,
          payment_method_id: data.paymentMethodId,
          promotion_code: data.promotionCode,
        },
      }
    );

    return this.transformSubscriptionResponse(response.data);
  }

  /**
   * Validate a promo code against Stripe
   * Returns discount details if valid
   */
  async validatePromoCode(code: string): Promise<PromoCodeValidationResponse> {
    const { data: response } = await apiClient.post<{ data: any }>(
      '/v1/promo_codes/validate',
      { code }
    );

    return {
      valid: response.data.valid,
      promotionCodeId: response.data.promotion_code_id,
      code: response.data.code,
      discount: {
        type: response.data.discount.type,
        percentOff: response.data.discount.percent_off,
        amountOff: response.data.discount.amount_off,
        currency: response.data.discount.currency,
        duration: response.data.discount.duration,
        durationInMonths: response.data.discount.duration_in_months,
      },
      restrictions: {
        firstTimeTransaction: response.data.restrictions.first_time_transaction,
        minimumAmount: response.data.restrictions.minimum_amount,
        minimumAmountCurrency: response.data.restrictions.minimum_amount_currency,
      },
    };
  }

  /**
   * Get list of subscriptions for the organization
   */
  async listSubscriptions(): Promise<Subscription[]> {
    const { data: response } = await apiClient.get<{ data: any[] }>('/v1/subscriptions');

    return response.data.map((sub) => this.transformSubscription(sub));
  }

  /**
   * Get current active subscription
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse | null> {
    try {
      const { data: response } = await apiClient.get<{ data: any }>(
        '/v1/subscriptions/current'
      );

      if (!response.data) {
        return null;
      }

      // The API returns { subscription: {...all fields including next_billing_date/amount...} }
      // We need to extract the subscription object and pull out the billing fields
      const subscriptionData = response.data.subscription;
      
      if (!subscriptionData) {
        return null;
      }

      const transformed: SubscriptionResponse = {
        subscription: this.transformSubscription(subscriptionData),
        nextBillingDate: subscriptionData.next_billing_date || null,
        nextBillingAmount: subscriptionData.next_billing_amount !== undefined ? subscriptionData.next_billing_amount : null,
        nextBillingCurrency: subscriptionData.next_billing_currency || subscriptionData.currency || 'USD',
      };
      
      return transformed;
    } catch (error: any) {
      // If 404, no active subscription exists
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Change subscription plan
   */
  async changeSubscriptionPlan(
    id: number,
    data: ChangeSubscriptionRequest
  ): Promise<SubscriptionResponse> {
    const { data: response } = await apiClient.put<{ data: any }>(
      `/v1/subscriptions/${id}`,
      {
        subscription: {
          new_plan_id: data.newPlanId,
          billing_cycle: data.billingCycle,
        },
      }
    );

    return this.transformSubscriptionResponse(response.data);
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(cancelImmediately = false): Promise<any> {
    const { data: response } = await apiClient.post<{ data: any }>(
      `/v1/subscriptions/cancel`,
      {
        cancel_immediately: cancelImmediately,
      }
    );

    return response.data;
  }

  /**
   * Resume a canceled subscription (before end of current period)
   */
  async resumeSubscription(): Promise<any> {
    const { data: response } = await apiClient.post<{ data: any }>(
      `/v1/subscriptions/resume`
    );

    return response.data;
  }

  /**
   * Get subscription limits and feature availability
   */
  async getSubscriptionLimits(): Promise<SubscriptionLimits> {
    const { data: response } = await apiClient.get<{ data: any }>(
      '/v1/admin/subscriptions/limits'
    );

    return this.transformSubscriptionLimits(response.data);
  }

  /**
   * Get available subscription plans (public endpoint)
   * @param currency Optional currency override (usd, eur, brl)
   */
  async getPlans(currency?: Currency): Promise<ApiPlansResponse> {
    const params = currency ? { currency } : {};
    const { data: response } = await apiClient.get<ApiPlansResponse>(
      '/v1/subscription_plans',
      { params }
    );

    return response;
  }

  /**
   * Get Stripe Customer Portal URL
   * Opens Stripe's hosted portal for managing subscriptions, payment methods, and invoices
   * @param locale Current app language (en, pt, de)
   */
  async getBillingPortalUrl(locale?: string): Promise<string> {
    const { data: response } = await apiClient.get<{ data: { url: string } }>(
      '/v1/subscriptions/billing_portal',
      { params: { locale } }
    );

    return response.data.url;
  }

  /**
   * Get Stripe Customer Session for pricing table
   * Creates a customer session that allows the pricing table to show
   * the user's current plan and "Switch plan" buttons instead of "Subscribe"
   */
  async getCustomerSession(): Promise<{ clientSecret: string } | null> {
    try {
      const { data: response } = await apiClient.get<{ data: { client_secret: string } }>(
        '/v1/subscriptions/customer_session'
      );

      return { clientSecret: response.data.client_secret };
    } catch (error: any) {
      // If 404 or 422, user doesn't have a Stripe customer yet
      if (error.response?.status === 404 || error.response?.status === 422) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get all available subscription plans from Stripe
   */
  async getStripePlans(): Promise<SubscriptionPlan[]> {
    const { data: response } = await apiClient.get<{ plans: any[] }>(
      '/v1/subscription_plans/stripe_plans'
    );

    return response.plans.map((plan: any) => ({
      id: plan.id,
      name: plan.name,
      description: plan.description,
      metadata: plan.metadata,
      marketingFeatures: plan.marketing_features || [],
      limits: {
        maxUsers: plan.limits?.max_users ?? null,
      },
      prices: plan.prices.map((price: any) => ({
        id: price.id,
        currency: price.currency,
        unitAmount: price.unit_amount,
        recurring: price.recurring ? {
          interval: price.recurring.interval,
          intervalCount: price.recurring.interval_count,
        } : null,
        type: price.type,
      })),
    }));
  }

  /**
   * Create a Stripe Checkout Session for a new subscription. The server picks the Price for
   * `{plan, interval}`; the client never sends a Price id.
   * @param plan Plan code (from the catalog's `metadata.plan_code`)
   * @param interval Billing interval
   * @param locale Current app language (en, es, pt-BR)
   * @param currency Billing currency (usd, eur, brl, pln)
   */
  async createCheckoutSession(plan: string, interval: BillingInterval, locale?: string, currency?: string): Promise<string> {
    const { data: response } = await apiClient.post<{ data: { url: string } }>(
      '/v1/subscriptions/checkout',
      { plan, interval, locale, currency },
    );

    return response.data.url;
  }

  /**
   * Move the current subscription to another plan or interval, in the app (prorated by Stripe).
   * Answers 422 `too_many_teammates` when the team is bigger than the plan's seats.
   */
  async changePlan(plan: string, interval: BillingInterval): Promise<void> {
    await apiClient.post('/v1/subscriptions/change_plan', { plan, interval });
  }

  /**
   * The organization's billing state, plan, seats and trial end.
   */
  async getBillingStatus(): Promise<BillingStatus> {
    const { data: response } = await apiClient.get<{ data: BillingStatus }>('/v1/billing/status');

    return response.data;
  }

  // Transformation methods
  private transformSubscription(apiSub: any): Subscription {
    return {
      id: apiSub.id,
      organizationId: apiSub.organization_id,
      planId: apiSub.plan_id,
      planCode: apiSub.plan_code,
      planName: apiSub.plan_name,
      status: apiSub.status,
      billingCycle: apiSub.billing_cycle,
      currency: apiSub.currency,
      currentPeriodStart: apiSub.current_period_start,
      currentPeriodEnd: apiSub.current_period_end,
      trialStart: apiSub.trial_start,
      trialEnd: apiSub.trial_end,
      cancelAt: apiSub.cancel_at,
      canceledAt: apiSub.canceled_at,
      createdAt: apiSub.created_at,
      updatedAt: apiSub.updated_at,
    };
  }

  private transformSubscriptionResponse(apiResponse: any): SubscriptionResponse {
    return {
      subscription: this.transformSubscription(apiResponse.subscription),
      nextBillingDate: apiResponse.next_billing_date,
      nextBillingAmount: apiResponse.next_billing_amount,
      nextBillingCurrency: apiResponse.next_billing_currency,
    };
  }

  private transformSubscriptionLimits(apiData: any): SubscriptionLimits {
    // A limit of 9999 or more means "unlimited". Seats are the only metered limit.
    const isUnlimited = (limit: number) => limit >= 9999;

    return {
      currentPlan: {
        code: apiData.current_plan.code,
        name: apiData.current_plan.name,
        displayName: apiData.current_plan.display_name,
        description: apiData.current_plan.description,
      },
      usage: {
        users: {
          current: apiData.usage.max_users.current,
          limit: apiData.usage.max_users.limit,
          percentage: apiData.usage.max_users.percentage,
          unlimited: isUnlimited(apiData.usage.max_users.limit),
        },
      },
      features: {
        emailNotifications: apiData.features.email_notifications,
        smsNotifications: apiData.features.sms_notifications,
        dataExport: apiData.features.data_export,
        apiAccess: apiData.features.api_access,
        webhooks: apiData.features.webhooks,
        customIntegrations: apiData.features.custom_integrations,
        teamCollaboration: apiData.features.team_collaboration,
        advancedAnalytics: apiData.features.advanced_analytics,
        customDomain: apiData.features.custom_domain,
        whiteLabel: apiData.features.white_label,
        prioritySupport: apiData.features.priority_support,
        sla: apiData.features.sla,
      },
      metadata: apiData.metadata,
    };
  }
}

export const subscriptionService = new SubscriptionService();
